import { Disc, ListPlus, Plus } from "lucide-react";
import React, { useState } from "react";
import { useLocalLibraryStore } from "../../stores/localLibraryStore";
import { usePlayerStore } from "../../stores/playerStore";
import { useRightSidebarStore } from "../../stores/rightSidebarStore";

export const AddToAlbum = () => {
  const [newAlbumName, setNewAlbumName] = useState("");
  const { getAllAlbums, addAlbum, addTrackToAlbum } = useLocalLibraryStore();
  const { currentTrack } = usePlayerStore();
  const { closeSidebar } = useRightSidebarStore();
  const albums = getAllAlbums();

  const handleAddToAlbum = (albumId: string) => {
    if (!currentTrack) return;

    const album = albums.find((a) => a.id === albumId);
    if (!album || album.tracks.some((t) => t.id === currentTrack.id)) {
      return; // Track already exists in album
    }

    addTrackToAlbum(albumId, currentTrack);
  };

  const handleCreateAlbum = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAlbumName.trim() || !currentTrack) return;

    // Provide default values for additional required parameters
    const albumId = await addAlbum(
      newAlbumName,
      currentTrack.artist || "Unknown Artist",
      "Created from current track",
      currentTrack.albumArt || ""
    );

    addTrackToAlbum(albumId, currentTrack);
    setNewAlbumName("");
  };

  return (
    <div className="p-6 space-y-6">
      {albums.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center space-y-4 bg-zinc-800/50 p-8 rounded-lg">
          <ListPlus className="w-16 h-16 text-zinc-500" />
          <div>
            <h3 className="text-lg font-semibold mb-2">No Albums Yet</h3>
            <p className="text-zinc-400 mb-4">Create your first album and start organizing your music collection</p>
          </div>
        </div>
      ) : (
        <>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Disc className="w-5 h-5" />
            Your Albums
          </h3>
          <div className="space-y-2">
            {albums.map((album) => {
              const hasTrack = currentTrack && album.tracks.some((t) => t.id === currentTrack.id);
              return (
                <button
                  key={album.id}
                  onClick={() => handleAddToAlbum(album.id)}
                  disabled={hasTrack}
                  className={`w-full px-4 py-3 text-left rounded-lg transition flex items-center justify-between ${
                    hasTrack ? "bg-zinc-800/50 text-zinc-500 cursor-not-allowed" : "bg-zinc-800 hover:bg-zinc-700"
                  }`}
                >
                  <span>{album.name}</span>
                  <span className="text-sm text-zinc-400">{album.tracks.length} tracks</span>
                </button>
              );
            })}
          </div>
        </>
      )}

      <div className="pt-6 border-t border-zinc-800">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Plus className="w-5 h-5" />
          Create New Album
        </h3>
        <form onSubmit={handleCreateAlbum} className="flex gap-2">
          <input
            type="text"
            value={newAlbumName}
            onChange={(e) => setNewAlbumName(e.target.value)}
            placeholder="Album name"
            className="flex-1 px-4 py-2 bg-zinc-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
          />
          <button
            type="submit"
            disabled={!newAlbumName.trim()}
            className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Create
          </button>
        </form>
      </div>
    </div>
  );
};
